import { AnimatePresence, motion, useMotionTemplate, useScroll, useSpring, useTransform } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import getPlatform from '../../../common/utils/getPlatform';
import AppDownloadModal2 from '../../AppDownloadModal2';
import { BrandSitesV2Theme } from '../common/GlobalStyle';
import useBreakpoints from '../hooks/useBreakpoints';

interface ObjectPosition {
  desktop: string;
  tablet: string;
  mobile: string;
}

const IMAGE_COUNT = 4;

const objectPositions: ObjectPosition[] = [
  {
    desktop: '50% 63%',
    tablet: '50% 42%',
    mobile: '50% 73%',
  },
  {
    desktop: '100% 44%',
    tablet: '50% 66%',
    mobile: '50% 63%',
  },
  {
    desktop: '100% 82%',
    tablet: '50% 60%',
    mobile: '50% 67%',
  },
  {
    desktop: '100% 37%',
    tablet: '50% 42%',
    mobile: '50% 68%',
  },
  {
    desktop: '100% 55%',
    tablet: '50% 32%',
    mobile: '50% 73%',
  },
];

export default function HeroSection() {
  const [imageIndex, setImageIndex] = useState(1);
  const [isAppDownloadModalVisible, setIsAppDownloadModalVisible] = useState(false);
  const media = useBreakpoints({ defaultMedia: 'unknown' });

  const nextImage = () => {
    if (document.visibilityState !== 'visible') {
      setTimeout(nextImage, 3000);
      return;
    }

    if (imageIndex === IMAGE_COUNT) setImageIndex(1);
    else setImageIndex(imageIndex + 1);
  };

  useEffect(() => {
    setTimeout(nextImage, 3000);
  }, [imageIndex]);

  useEffect(() => {
    // 이미지 프리로드
    if (media === 'unknown') return;

    for (let i = 1; i <= IMAGE_COUNT; i++) {
      const image = new Image();
      image.src = `https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/hero${i}_${media}.webp`;
    }
  }, [media]);

  const ref = useRef<HTMLImageElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useSpring(useTransform(scrollYProgress, [0.6, 1], [0, 10]), {
    stiffness: 600,
    damping: 100,
  });
  const transform = useMotionTemplate`translateY(${y}%)`;

  return (
    <Container $imageIndex={imageIndex}>
      <HeroImageContainer>
        <HeroImageWrapper ref={ref} style={{ transform }}>
          <AnimatePresence initial={true}>
            <picture key={imageIndex}>
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/hero${imageIndex}_mobile.webp`}
                type="image/webp"
                media={`(max-width:${BrandSitesV2Theme!.brandSitesV2!.breakpoints.mobile})`}
              />
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/hero${imageIndex}_tablet.webp`}
                type="image/webp"
                media={`(max-width:${BrandSitesV2Theme!.brandSitesV2!.breakpoints.tablet})`}
              />
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/webp/hero${imageIndex}_desktop.webp`}
                type="image/webp"
              />
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/hero${imageIndex}_mobile.png`}
                type="image/png"
                media={`(max-width:${BrandSitesV2Theme!.brandSitesV2!.breakpoints.mobile})`}
              />
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/hero${imageIndex}_tablet.png`}
                type="image/png"
                media={`(max-width:${BrandSitesV2Theme!.brandSitesV2!.breakpoints.tablet})`}
              />
              <source
                srcSet={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/hero${imageIndex}_desktop.png`}
                type="image/png"
              />
              <HeroImage
                src={`https://cleanhome-web.cleaninglab.co.kr/brandsite/home/hero${imageIndex}_desktop.png`}
                alt=""
                initial={{ scale: 1, opacity: 0 }}
                animate={{
                  scale: [1, 1.04],
                  opacity: [0, 1],
                  transition: { duration: 1, times: [0, 1], ease: [0.65, 0, 0.35, 1] },
                }}
                exit={{
                  opacity: 0,
                  transition: {
                    duration: 0.8,
                    times: [0, 1],
                    ease: [0.65, 0, 0.35, 1],
                  },
                }}
                key={imageIndex}
                $imageIndex={imageIndex}
              />
            </picture>
          </AnimatePresence>
        </HeroImageWrapper>
      </HeroImageContainer>
      <HeroFrontWrapper>
        <HeroFront>
          <HeroTitleWrapper
            initial={{ opacity: 0 }}
            animate={{ y: [10, 10, 0], opacity: [0, 0, 1] }}
            transition={{ duration: 1, times: [0, 0.6, 1], ease: [0.65, 0, 0.35, 1] }}
          >
            <FakeHeroTitle>청소를 바꾸다 생활을 바꾸다</FakeHeroTitle>
            <HeroTitleDesktop>{HeroTitleDesktopSvg}</HeroTitleDesktop>
            <HeroTitleTablet>{HeroTitleTabletSvg}</HeroTitleTablet>
          </HeroTitleWrapper>
          <HeroBottom
            initial={{ opacity: 0 }}
            animate={{ y: [10, 10, 0], opacity: [0, 0, 1] }}
            transition={{ duration: 1.2, times: [0, 0.66, 1], ease: [0.65, 0, 0.35, 1] }}
          >
            <Description>
              청소, 세탁, 설거지 끝없는 집안일은 그만 <wbr /> 깨끗한 집에서 일상의 행복을 채워가요.
            </Description>
            <Link href="https://res.cleaninglab.co.kr/u/web_to_app.html" passHref>
              <Button
                onClick={(e) => {
                  const platform = getPlatform();

                  if (platform === 'mobile' || platform === 'tablet') return;

                  e.preventDefault();
                  setIsAppDownloadModalVisible(true);
                }}
              >
                앱 다운로드
              </Button>
            </Link>
          </HeroBottom>
        </HeroFront>
      </HeroFrontWrapper>
      <AppDownloadModal2
        visible={isAppDownloadModalVisible}
        setVisible={setIsAppDownloadModalVisible}
        hasBodyScrollLock
      />
    </Container>
  );
}

const HeroImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const HeroImageWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
`;

const HeroImage = styled(motion.img)<{ $imageIndex: number }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  max-height: 100vh;
  object-fit: cover;

  object-position: ${({ $imageIndex }) => objectPositions[$imageIndex - 1]?.desktop || 'center'};

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    object-position: ${({ $imageIndex }) => objectPositions[$imageIndex - 1]?.tablet || 'center'};
    transform-origin: left bottom;
    transform-origin: center center;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    transform-origin: center center;
    object-position: ${({ $imageIndex }) => objectPositions[$imageIndex - 1]?.mobile || 'center'};
  }
`;

const HeroFrontWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0 24px;
`;

const HeroFront = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.desktop};
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    max-width: ${({ theme }) => theme.brandSitesV2!.containerWidths.tablet};
  }
`;

const HeroTitleWrapper = styled(motion.div)`
  margin-top: 174px;
  margin-bottom: 40px;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    margin-top: 124px;
    margin-bottom: 30px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    margin-top: 114px;
    margin-bottom: 24px;
  }

  svg path {
    transition: 0.8s;
  }
`;

const FakeHeroTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  margin: -1px;
  clip: rect(0, 0, 0, 0);
  text-indent: -5000px;
`;

const HeroTitleDesktop = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: none;
  }
`;

const HeroTitleTablet = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    display: block;
  }
`;

const HeroBottom = styled(motion.div)``;

const Description = styled.p`
  color: rgba(0, 0, 0, 0.6);
  font-weight: 300;
  font-size: 21px;
  line-height: 140%;
  word-break: keep-all;
  max-width: 340px;
  margin-bottom: 40px;
  transition: 0.8s;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    font-size: 17px;
    margin-bottom: 48px;
    max-width: 280px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    font-weight: 400;
  }
`;

const Button = styled.a`
  display: inline-block;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  border: 1px solid #42484c;
  border-radius: 8px;
  padding: 12px 20px;
  transition: 0.2s;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    padding: 10px 16px;
    font-size: 15px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    padding: 9px 16px;
  }

  &:hover {
    color: #ffffff;
    background: #42484c;
  }
`;

const Container = styled.section<{ $imageIndex: number }>`
  position: relative;
  width: 100%;
  height: 700px;
  max-height: 100vh;

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.tablet}) {
    height: 600px;
  }

  @media (max-width: ${({ theme }) => theme.brandSitesV2!.breakpoints.mobile}) {
    height: 620px;
  }

  /* ${({ $imageIndex }) => {
    if ($imageIndex === 3) {
      return css`
        ${HeroTitleWrapper} svg path {
          fill: #ffffff;
        }
        ${Description} {
          color: #ffffff;
        }
        ${Button} {
          color: #ffffff;
          border: 1px solid #ffffff;

          &:hover {
            transition: 0.8s;
            background-color: #ffffff;
            color: rgba(0, 0, 0, 0.6);
            border: 1px solid #ffffff;
          }
        }
      `;
    }
  }} */
`;

const HeroTitleDesktopSvg = (
  <svg width="305" height="122" viewBox="0 0 305 122" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M23.9139 6.172V0.609999H8.36189V6.172H23.9139ZM36.2259 41.38C36.2259 44.566 33.2559 46.564 29.3139 46.564C25.2639 46.564 22.4559 44.566 22.4559 41.38C22.4559 38.248 25.2639 36.25 29.3139 36.25C33.2559 36.25 36.2259 38.248 36.2259 41.38ZM43.1919 30.58V0.555999H36.6039V15.73H28.8819V21.13H36.6039V30.58H43.1919ZM29.3139 14.38V8.818H2.90789V14.38H12.5199C11.7099 20.158 6.79589 23.722 1.50389 25.342L4.47389 30.58C9.76589 28.96 14.6799 24.802 16.2999 21.13C17.9739 24.748 23.4819 28.474 28.0179 29.554L30.8259 24.208C25.2099 22.804 20.7819 19.888 20.0259 14.38H29.3139ZM42.7599 41.38C42.7599 35.17 37.0899 30.742 29.3139 30.742C21.5379 30.742 15.8679 35.17 15.8679 41.38C15.8679 47.806 21.5379 52.072 29.3139 52.072C37.0899 52.072 42.7599 47.806 42.7599 41.38ZM94.5417 40.624V35.116H75.3717V24.91H68.7297V35.116H49.5057V40.624H94.5417ZM68.1897 1.852V4.822C68.1897 13.57 60.7917 19.078 52.7997 21.238L55.7697 26.908C63.1677 24.64 69.2697 20.266 72.0237 14.38C74.9397 20.05 80.7177 24.424 88.3317 26.8L91.3017 21.184C83.4177 18.916 75.8577 13.57 75.8577 4.822V1.852H68.1897ZM137.197 51.586V46.402H111.817V43.864H135.901V31.012H105.283V36.196H129.367V38.788H105.283V51.586H137.197ZM143.137 28.582V23.452H98.1014V28.582H143.137ZM136.819 21.076V16H112.087V13.462H135.631V0.933998H105.553V6.064H129.097V8.44H105.553V21.076H136.819ZM207.624 20.428V14.92H199.578V0.663998H192.99V45.214H199.578V20.428H207.624ZM185.646 32.308V2.878H179.058V12.436H168.852V2.878H162.264V32.308H185.646ZM179.058 26.746H168.852V18.052H179.058V26.746ZM235.214 49.21V29.392H254.384V23.722H246.878C248.282 17.836 249.146 9.196 248.984 2.608H232.082V8.17H242.18C242.45 12.382 241.532 18.916 240.128 23.722H226.412C228.356 17.998 229.49 10.276 229.274 2.608H213.344V8.17H222.47C222.416 12.76 221.498 18.646 219.554 23.722H209.348V29.392H228.572V49.21H235.214ZM259.564 32.686C268.798 33.118 279.706 32.794 287.212 31.282L286.618 25.72C280.516 26.908 271.822 27.178 266.152 26.854V9.142H282.514V3.634H259.564V32.686ZM304.816 20.428V14.92H296.77V0.663998H290.182V45.214H296.77V20.428H304.816ZM36.6579 110.678C36.6579 114.134 33.7419 116.402 29.6919 116.402C25.4799 116.402 22.4559 114.134 22.4559 110.678C22.4559 107.33 25.4799 105.008 29.6919 105.008C33.7419 105.008 36.6579 107.33 36.6579 110.678ZM25.2099 92.642C19.8099 89.996 16.1919 84.542 16.1919 77.198V72.986H9.54989V77.36C9.54989 84.65 5.98589 90.536 0.0998907 93.992L4.09589 98.798C7.92989 96.584 11.6559 92.48 13.0599 88.484C14.4099 92.156 17.9199 95.774 21.4299 97.772L25.2099 92.642ZM43.8399 99.554V70.556H37.4139V81.572H32.9859V71.258H26.5599V97.124H32.9859V87.08H37.4139V99.554H43.8399ZM43.3539 110.678C43.3539 104.144 37.6299 99.554 29.6919 99.554C21.6459 99.554 15.8679 104.144 15.8679 110.678C15.8679 117.536 21.6459 121.964 29.6919 121.964C37.6299 121.964 43.3539 117.536 43.3539 110.678ZM64.6257 83.786C67.8117 83.786 69.3237 85.352 69.3237 86.918C69.3237 88.43 67.8117 90.05 64.6257 90.05C61.5477 90.05 59.9277 88.43 59.9277 86.918C59.9277 85.352 61.5477 83.786 64.6257 83.786ZM71.1597 75.848V70.61H57.8757V75.848H71.1597ZM96.1077 89.51V83.948H88.7097V70.61H82.1217V100.31H88.7097V89.51H96.1077ZM89.6277 121.802V116.726H65.7057V114.674H88.7097V103.01H59.0637V108.032H82.1757V110.084H59.1717V121.802H89.6277ZM75.6957 87.188C75.6957 85.676 75.1017 84.272 74.0757 83.084H79.0437V78.224H49.3977V83.084H55.2837C54.2577 84.272 53.7177 85.622 53.7177 87.134C53.7177 90.806 56.4717 93.722 61.3857 94.586V96.314C57.0657 96.422 51.9897 96.314 48.8577 96.206L49.5057 101.39C53.9877 101.444 60.4137 101.282 64.7877 101.012C70.4577 100.796 76.0737 100.256 79.6377 99.77L79.0977 95.072C76.5597 95.504 72.5097 95.828 67.9737 96.098V94.586C73.1037 93.722 75.6957 90.806 75.6957 87.188ZM128.017 80.816C128.017 83.948 124.885 86.27 120.619 86.27C116.191 86.27 113.221 83.948 113.221 80.816C113.221 77.954 116.245 75.47 120.619 75.47C124.885 75.47 128.017 77.954 128.017 80.816ZM134.659 80.816C134.659 74.12 128.503 70.016 120.619 70.016C113.059 70.016 106.579 74.066 106.579 80.816C106.579 88.16 113.653 91.616 120.673 91.616C127.639 91.616 134.659 88.106 134.659 80.816ZM136.927 121.748V116.51H112.141V113.648H135.631V100.85H105.607V105.98H129.097V108.788H105.607V121.748H136.927ZM143.137 98.258V92.966H98.1014V98.258H143.137ZM207.624 90.428V84.92H199.578V70.664H192.99V115.214H199.578V90.428H207.624ZM185.646 102.308V72.878H179.058V82.436H168.852V72.878H162.264V102.308H185.646ZM179.058 96.746H168.852V88.052H179.058V96.746ZM235.214 119.21V99.392H254.384V93.722H246.878C248.282 87.836 249.146 79.196 248.984 72.608H232.082V78.17H242.18C242.45 82.382 241.532 88.916 240.128 93.722H226.412C228.356 87.998 229.49 80.276 229.274 72.608H213.344V78.17H222.47C222.416 82.76 221.498 88.646 219.554 93.722H209.348V99.392H228.572V119.21H235.214ZM259.564 102.686C268.798 103.118 279.706 102.794 287.212 101.282L286.618 95.72C280.516 96.908 271.822 97.178 266.152 96.854V79.142H282.514V73.634H259.564V102.686ZM304.816 90.428V84.92H296.77V70.664H290.182V115.214H296.77V90.428H304.816Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);

const HeroTitleTabletSvg = (
  <svg width="193" height="77" viewBox="0 0 193 77" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.946 3.812V0.309999H6.154V3.812H15.946ZM23.698 25.98C23.698 27.986 21.828 29.244 19.346 29.244C16.796 29.244 15.028 27.986 15.028 25.98C15.028 24.008 16.796 22.75 19.346 22.75C21.828 22.75 23.698 24.008 23.698 25.98ZM28.084 19.18V0.276H23.936V9.83H19.074V13.23H23.936V19.18H28.084ZM19.346 8.98V5.478H2.72V8.98H8.772C8.262 12.618 5.168 14.862 1.836 15.882L3.706 19.18C7.038 18.16 10.132 15.542 11.152 13.23C12.206 15.508 15.674 17.854 18.53 18.534L20.298 15.168C16.762 14.284 13.974 12.448 13.498 8.98H19.346ZM27.812 25.98C27.812 22.07 24.242 19.282 19.346 19.282C14.45 19.282 10.88 22.07 10.88 25.98C10.88 30.026 14.45 32.712 19.346 32.712C24.242 32.712 27.812 30.026 27.812 25.98ZM60.4153 25.504V22.036H48.3453V15.61H44.1633V22.036H32.0593V25.504H60.4153ZM43.8233 1.092V2.962C43.8233 8.47 39.1653 11.938 34.1333 13.298L36.0033 16.868C40.6613 15.44 44.5033 12.686 46.2373 8.98C48.0733 12.55 51.7113 15.304 56.5053 16.8L58.3753 13.264C53.4113 11.836 48.6513 8.47 48.6513 2.962V1.092H43.8233ZM87.2727 32.406V29.142H71.2927V27.544H86.4567V19.452H67.1787V22.716H82.3427V24.348H67.1787V32.406H87.2727ZM91.0127 17.922V14.692H62.6567V17.922H91.0127ZM87.0347 13.196V10H71.4627V8.402H86.2867V0.513999H67.3487V3.744H82.1727V5.24H67.3487V13.196H87.0347ZM131.615 12.788V9.32H126.549V0.343999H122.401V28.394H126.549V12.788H131.615ZM117.777 20.268V1.738H113.629V7.756H107.203V1.738H103.055V20.268H117.777ZM113.629 16.766H107.203V11.292H113.629V16.766ZM148.987 30.91V18.432H161.057V14.862H156.331C157.215 11.156 157.759 5.716 157.657 1.568H147.015V5.07H153.373C153.543 7.722 152.965 11.836 152.081 14.862H143.445C144.669 11.258 145.383 6.396 145.247 1.568H135.217V5.07H140.963C140.929 7.96 140.351 11.666 139.127 14.862H132.701V18.432H144.805V30.91H148.987ZM164.318 20.506C170.132 20.778 177 20.574 181.726 19.622L181.352 16.12C177.51 16.868 172.036 17.038 168.466 16.834V5.682H178.768V2.214H164.318V20.506ZM192.81 12.788V9.32H187.744V0.343999H183.596V28.394H187.744V12.788H192.81ZM23.97 69.538C23.97 71.714 22.134 73.142 19.584 73.142C16.932 73.142 15.028 71.714 15.028 69.538C15.028 67.43 16.932 65.968 19.584 65.968C22.134 65.968 23.97 67.43 23.97 69.538ZM16.762 58.182C13.362 56.516 11.084 53.082 11.084 48.458V45.806H6.902V48.56C6.902 53.15 4.658 56.856 0.952 59.032L3.468 62.058C5.882 60.664 8.228 58.08 9.112 55.564C9.962 57.876 12.172 60.154 14.382 61.412L16.762 58.182ZM28.492 62.534V44.276H24.446V51.212H21.658V44.718H17.612V61.004H21.658V54.68H24.446V62.534H28.492ZM28.186 69.538C28.186 65.424 24.582 62.534 19.584 62.534C14.518 62.534 10.88 65.424 10.88 69.538C10.88 73.856 14.518 76.644 19.584 76.644C24.582 76.644 28.186 73.856 28.186 69.538ZM41.5793 52.606C43.5853 52.606 44.5373 53.592 44.5373 54.578C44.5373 55.53 43.5853 56.55 41.5793 56.55C39.6413 56.55 38.6213 55.53 38.6213 54.578C38.6213 53.592 39.6413 52.606 41.5793 52.606ZM45.6933 47.608V44.31H37.3293V47.608H45.6933ZM61.4013 56.21V52.708H56.7433V44.31H52.5953V63.01H56.7433V56.21H61.4013ZM57.3213 76.542V73.346H42.2593V72.054H56.7433V64.71H38.0773V67.872H52.6293V69.164H38.1453V76.542H57.3213ZM48.5493 54.748C48.5493 53.796 48.1753 52.912 47.5293 52.164H50.6573V49.104H31.9913V52.164H35.6973C35.0513 52.912 34.7113 53.762 34.7113 54.714C34.7113 57.026 36.4453 58.862 39.5393 59.406V60.494C36.8193 60.562 33.6233 60.494 31.6513 60.426L32.0593 63.69C34.8813 63.724 38.9273 63.622 41.6813 63.452C45.2513 63.316 48.7873 62.976 51.0313 62.67L50.6913 59.712C49.0933 59.984 46.5433 60.188 43.6873 60.358V59.406C46.9173 58.862 48.5493 57.026 48.5493 54.748ZM81.4927 50.736C81.4927 52.708 79.5207 54.17 76.8347 54.17C74.0467 54.17 72.1767 52.708 72.1767 50.736C72.1767 48.934 74.0807 47.37 76.8347 47.37C79.5207 47.37 81.4927 48.934 81.4927 50.736ZM85.6747 50.736C85.6747 46.52 81.7987 43.936 76.8347 43.936C72.0747 43.936 67.9947 46.486 67.9947 50.736C67.9947 55.36 72.4487 57.536 76.8687 57.536C81.2547 57.536 85.6747 55.326 85.6747 50.736ZM87.1027 76.508V73.21H71.4967V71.408H86.2867V63.35H67.3827V66.58H82.1727V68.348H67.3827V76.508H87.1027ZM91.0127 61.718V58.386H62.6567V61.718H91.0127ZM131.615 56.788V53.32H126.549V44.344H122.401V72.394H126.549V56.788H131.615ZM117.777 64.268V45.738H113.629V51.756H107.203V45.738H103.055V64.268H117.777ZM113.629 60.766H107.203V55.292H113.629V60.766ZM148.987 74.91V62.432H161.057V58.862H156.331C157.215 55.156 157.759 49.716 157.657 45.568H147.015V49.07H153.373C153.543 51.722 152.965 55.836 152.081 58.862H143.445C144.669 55.258 145.383 50.396 145.247 45.568H135.217V49.07H140.963C140.929 51.96 140.351 55.666 139.127 58.862H132.701V62.432H144.805V74.91H148.987ZM164.318 64.506C170.132 64.778 177 64.574 181.726 63.622L181.352 60.12C177.51 60.868 172.036 61.038 168.466 60.834V49.682H178.768V46.214H164.318V64.506ZM192.81 56.788V53.32H187.744V44.344H183.596V72.394H187.744V56.788H192.81Z"
      fill="black"
      fillOpacity="0.85"
    />
  </svg>
);
